
import { Injectable } from '@angular/core';
import { FilterInfo } from '../models/filter-info.model';
import { IHandlerServer } from './handler';
import { GridColumn, GridColumnChildren } from '../grid-control.model';

@Injectable({
  providedIn: 'root'
})
export class HandlerDotnet implements IHandlerServer {
  mapRequest = (filterInfo: FilterInfo) => {
    const result = {
      whereClause: '1>0',
      pageSize: filterInfo.pageSize,
      pageNumber: filterInfo.pageNumber,
      orderBy: filterInfo.orderBy,
      orderDirection: filterInfo.orderDirection,
      filter: {},
      externalFilter: {}
    };
    if (filterInfo.customFilter) {
      // Nếu custom filter là string
      if (typeof (filterInfo.customFilter) === 'string') {
        result.whereClause += ` AND ${filterInfo.customFilter}`;
      } else if (typeof (filterInfo.customFilter) === 'function') {
        result.whereClause += ` AND ${filterInfo.customFilter()}`;
      }
    }
    const executeColumnNoChildren = (column: Exclude<GridColumn, GridColumnChildren>) => {
      if (!filterInfo.rawFilter[column.field]) {
        return;
      }
      result.filter[column.field] = filterInfo.rawFilter[column.field];
      if (column.type === 'string') {
        const filterValue: string = (filterInfo.rawFilter[column.field] ?? '').toString();
        if (this.isGuid(filterValue)) {
          result.whereClause += ` AND ${column.field} = GUID("${filterInfo.rawFilter[column.field]}")`;
        } else if (filterValue.startsWith('!=')) {
          result.whereClause += ` AND ${column.field} != "${filterValue.substring(2, filterValue.length)}"`;
        } else if (filterValue.startsWith('=')) {
          result.whereClause += ` AND ${column.field} = "${filterValue.substring(1, filterValue.length)}"`;
        } else if (filterValue.startsWith('%')) {
          result.whereClause += ` AND ${column.field}.EndsWith("${filterValue.substring(1, filterValue.length)}")`;
        } else if (filterValue.endsWith('%')) {
          result.whereClause += ` AND ${column.field}.StartsWith("${filterValue.substring(0, filterValue.length - 1)}")`;
        } else {
          result.whereClause += ` AND ${column.field}.Contains("${filterValue}")`;
        }
      } else if (column.type === 'number') {
        const value = +(filterInfo.rawFilter[column.field].replace('>=', '').replace('<=', '').replace('>', '').replace('<', '').trim());
        if (filterInfo.rawFilter[column.field].indexOf('>=') > -1) {
          result.whereClause += ` AND ${column.field} >= ${value}`;
        } else if (filterInfo.rawFilter[column.field].indexOf('<=') > -1) {
          result.whereClause += ` AND ${column.field} <= ${value}`;
        } else if (filterInfo.rawFilter[column.field].indexOf('>') > -1) {
          result.whereClause += ` AND ${column.field} > ${value}`;
        } else if (filterInfo.rawFilter[column.field].indexOf('<') > -1) {
          result.whereClause += ` AND ${column.field} < ${value}`;
        } else {
          result.whereClause += ` AND ${column.field} = ${value}`;
        }
      } else if (column.type === 'bool') {
        if (filterInfo.rawFilter[column.field] === '0' || filterInfo.rawFilter[column.field] === '1') {
          result.whereClause += ` AND ${column.field} = ${filterInfo.rawFilter[column.field] === '1' ? 'true' : 'false'}`;
        }
      } else if (column.type === 'values') {
        if (filterInfo.rawFilter[column.field]) {
          if (this.isGuid(filterInfo.rawFilter[column.field])) {
            result.whereClause += ` AND ${column.field} = GUID("${filterInfo.rawFilter[column.field]}")`;
          } else {
            result.whereClause += ` AND ${column.field} = "${filterInfo.rawFilter[column.field]}"`;
          }
        }
      } else if (column.type === 'radio') {
        if (filterInfo.rawFilter[column.field]) {
          result.whereClause += ` AND ${column.field} = "${filterInfo.rawFilter[column.field]}"`;
        }
      } else if (column.type === 'date' || column.type === 'datetime') {
        // "DateTime(yyyy,mm,dd,HH,mm,ss)"
        // generate fromDate, toDate
        let date = new Date(filterInfo.rawFilter[column.field]);

        // From Date
        date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
        result.whereClause += ` AND ${column.field} >= DateTime(${date.getFullYear()},${date.getMonth() + 1},${date.getDate()})`;

        // To Date
        date.setDate(date.getDate() + 1);
        result.whereClause += ` AND ${column.field} < DateTime(${date.getFullYear()},${date.getMonth() + 1},${date.getDate()})`;
      }
    };
    const executeColumnChildren = (columnChildren: GridColumnChildren) => {
      let flag = false;
      for (const column of columnChildren.children) {
        if (filterInfo.rawFilter[column.field]) {
          executeColumnNoChildren(column);
          flag = true;
        }
      }
      if (flag) {
        return;
      }
      if (!filterInfo.rawFilter[columnChildren.field]) {
        return;
      }
      result.filter[columnChildren.field] = filterInfo.rawFilter[columnChildren.field];
      let whereClause = '';
      const filterValue: string = (filterInfo.rawFilter[columnChildren.field] ?? '').toString().trim();
      for (const column of columnChildren.children) {
        if (column.type === 'string') {
          if (this.isGuid(filterValue)) {
            whereClause += ` OR ${column.field} = GUID("${filterValue}")`;
          } else if (filterValue.startsWith('!=')) {
            whereClause += ` OR ${column.field} != "${filterValue.substring(2, filterValue.length)}"`;
          } else if (filterValue.startsWith('=')) {
            whereClause += ` OR ${column.field} = "${filterValue.substring(1, filterValue.length)}"`;
          } else if (filterValue.startsWith('%')) {
            whereClause += ` OR ${column.field}.EndsWith("${filterValue.substring(1, filterValue.length)}")`;
          } else if (filterValue.endsWith('%')) {
            whereClause += ` OR ${column.field}.StartsWith("${filterValue.substring(0, filterValue.length - 1)}")`;
          } else {
            whereClause += ` OR ${column.field}.Contains("${filterValue}")`;
          }
        } else if (column.type === 'number') {
          const value = +(filterValue.replace('>=', '').replace('<=', '').replace('>', '').replace('<', '').trim());
          if (!Number.isNaN(value)) {
            if (filterValue.indexOf('>=') > -1) {
              whereClause += ` OR ${column.field} >= ${value}`;
            } else if (filterValue.indexOf('<=') > -1) {
              whereClause += ` OR ${column.field} <= ${value}`;
            } else if (filterValue.indexOf('>') > -1) {
              whereClause += ` OR ${column.field} > ${value}`;
            } else if (filterValue.indexOf('<') > -1) {
              whereClause += ` OR ${column.field} < ${value}`;
            } else {
              whereClause += ` OR ${column.field} = ${value}`;
            }
          }
        } else if (column.type === 'bool') {
          if (filterValue === '0' || filterValue === '1') {
            whereClause += ` OR ${column.field} = ${filterValue === '1' ? 'true' : 'false'}`;
          }
        } else if (column.type === 'values') {
          if (this.isGuid(filterValue)) {
            whereClause += ` OR ${column.field} = GUID("${filterValue}")`;
          } else {
            whereClause += ` OR ${column.field} = "${filterValue}"`;
          }
        } else if (column.type === 'radio') {
          if (filterValue) {
            whereClause += ` OR ${column.field} = "${filterValue}"`;
          }
        } else if (column.type === 'date' || column.type === 'datetime') {
          // "DateTime(yyyy,mm,dd,HH,mm,ss)"
          // generate fromDate, toDate
          if (Date.isDate(filterValue)) {
            let date = new Date(filterValue);

            // From Date
            date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
            whereClause += ` OR (${column.field} >= DateTime(${date.getFullYear()},${date.getMonth() + 1},${date.getDate()})`;

            // To Date
            date.setDate(date.getDate() + 1);
            whereClause += ` AND ${column.field} < DateTime(${date.getFullYear()},${date.getMonth() + 1},${date.getDate()}))`;
          }
        }
      }
      if (whereClause.trim().startsWith('OR')) {
        whereClause = whereClause.trim().substr(2).trim();
        result.whereClause += ` AND(${whereClause})`;
      }
    };
    filterInfo.columns.forEach(column => {
      if (column.type !== 'children') {
        executeColumnNoChildren(column);
      } else {
        executeColumnChildren(column);
      }
    });
    filterInfo.externalFilters?.forEach(externalFilter => {
      const { field, type } = externalFilter;
      if (type === 'daterangefull') {
        result.externalFilter[field] = filterInfo.rawExternalFilter?.[field];
        if (result.externalFilter[field]?.from && result.externalFilter[field]?.to) {
          const fromDate = new Date(result.externalFilter[field]?.from);
          const toDate = Date.addDays(result.externalFilter[field]?.to, 1);
          // tslint:disable-next-line: max-line-length
          result.whereClause += ` AND ${field} >= DateTime(${fromDate.getFullYear()},${fromDate.getMonth() + 1},${fromDate.getDate()})`;
          result.whereClause += ` AND ${field} < DateTime(${toDate.getFullYear()},${toDate.getMonth() + 1},${toDate.getDate()})`;
        }
        return;
      }
    });
    return result;
  }

  mapResponse = (res: any) => {
    const { items, total } = res;
    return {
      items,
      total
    };
  }

  private isGuid = (key: string): boolean => {
    const regex = /[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/i;
    return regex.test(key);
  }
}
